import {
    ReactElement,
    useRef,
    useState,
    Dispatch,
    FormEventHandler,
    forwardRef,
    SetStateAction,
    useCallback,
    useEffect,
    useImperativeHandle
} from 'react';
import { useUpdateCompanyUserAction } from '../actions';
import {
    UIButton,
    UIFormInput,
    UIIconButton,
    UISection,
    UISectionContent,
    UISectionFooter,
    UISectionHeader,
    UISelectInput,
    UISelectValue,
    UIText
} from '@/ui';
import { useCompaniesTranslation } from '../hooks';
import { FullAccessCompanyUser } from '../util/company-user-access';

type MedicalSettingsSectionProps = {
    companyUser: FullAccessCompanyUser;
    canEdit: boolean;
    /** Shown on the disabled Edit button when `canEdit` is false. */
    editDisabledReason?: string;
};

export function MemberMedicalSettingsSection(
    props: MedicalSettingsSectionProps
): ReactElement {
    const { companyUser, canEdit, editDisabledReason } = props;
    const medical = companyUser.medical;

    const { t } = useCompaniesTranslation();

    const formRef = useRef<MemberMedicalSettingsSectionFormRef>(null);
    const [isEditing, setIsEditing] = useState(false);
    const updateCompanyUser = useUpdateCompanyUserAction({
        successMessage: t(
            'components.member_medical_settings_section.toast_updated'
        )
    });

    function onSubmit(data: {
        bloodType: string | null;
        allergies: string[];
        medicalConditions: string[];
        medications: string[];
    }) {
        const { bloodType, allergies, medicalConditions, medications } = data;
        updateCompanyUser
            .updateCompanyUser(companyUser.id, {
                medical: {
                    bloodType,
                    allergies,
                    conditions: medicalConditions,
                    medication: medications
                }
            })
            .then(() => {
                setIsEditing(false);
            })
            .catch(() => {}); // Error handled in the action
    }

    return (
        <MemberMedicalSettingsSectionForm
            ref={formRef}
            canEdit={canEdit}
            editDisabledReason={editDisabledReason}
            initialBloodType={medical.bloodType}
            initialAllergies={medical.allergies}
            initialMedicalConditions={medical.conditions}
            initialMedications={medical.medication}
            loading={updateCompanyUser.loading}
            onSubmit={onSubmit}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
        />
    );
}

type MedicalSettingsSectionFormProps = {
    canEdit: boolean;
    /** Shown on the disabled Edit button when `canEdit` is false. */
    editDisabledReason?: string;
    initialBloodType: string | null;
    initialAllergies: string[];
    initialMedicalConditions: string[];
    initialMedications: string[];
    loading: boolean;
    isEditing: boolean;
    setIsEditing: Dispatch<SetStateAction<boolean>>;
    onSubmit: (data: {
        bloodType: string | null;
        allergies: string[];
        medicalConditions: string[];
        medications: string[];
    }) => void;
};

type MemberMedicalSettingsSectionFormRef = {
    resetForm: () => void;
};

const MemberMedicalSettingsSectionForm = forwardRef<
    MemberMedicalSettingsSectionFormRef,
    MedicalSettingsSectionFormProps
>((props, ref): ReactElement => {
    const {
        canEdit,
        editDisabledReason,
        initialBloodType,
        initialAllergies,
        initialMedicalConditions,
        initialMedications,
        loading,
        isEditing,
        setIsEditing,
        onSubmit
    } = props;

    const { t } = useCompaniesTranslation();

    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * Blood Type
     */
    const bloodTypeOptions: UISelectValue[] = [
        {
            label: t(
                'components.member_medical_settings_section.blood_type_o_positive'
            ),
            value: 'O Positive'
        },
        {
            label: t(
                'components.member_medical_settings_section.blood_type_o_negative'
            ),
            value: 'O Negative'
        },
        {
            label: t(
                'components.member_medical_settings_section.blood_type_a_positive'
            ),
            value: 'A Positive'
        },
        {
            label: t(
                'components.member_medical_settings_section.blood_type_a_negative'
            ),
            value: 'A Negative'
        },
        {
            label: t(
                'components.member_medical_settings_section.blood_type_b_positive'
            ),
            value: 'B Positive'
        },
        {
            label: t(
                'components.member_medical_settings_section.blood_type_b_negative'
            ),
            value: 'B Negative'
        },
        {
            label: t(
                'components.member_medical_settings_section.blood_type_ab_positive'
            ),
            value: 'AB Positive'
        },
        {
            label: t(
                'components.member_medical_settings_section.blood_type_ab_negative'
            ),
            value: 'AB Negative'
        }
    ];
    const [bloodType, setBloodType] = useState(
        bloodTypeOptions.find((o) => o.value === initialBloodType)
    );

    /**
     * Allergies
     */
    const [allergies, setAllergies] = useState(initialAllergies);
    const [allergiesError, setAllergiesError] = useState<string[]>([]);
    const checkAllergies = useCallback(
        (allergies: string[]): string[] => {
            return allergies.map((item) => {
                const trimmed = item.trim();
                if (trimmed.length === 0) {
                    return t(
                        'components.member_medical_settings_section.list_item_error_required'
                    );
                }
                if (trimmed.length > 100) {
                    return t(
                        'components.member_medical_settings_section.list_item_error_too_long'
                    );
                }
                return '';
            });
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setAllergiesError(checkAllergies(allergies));
        }
    }, [allergies, showRealTimeErrors, checkAllergies]);

    /**
     * Medical Conditions
     */
    const [medicalConditions, setMedicalConditions] = useState(
        initialMedicalConditions
    );
    const [medicalConditionsError, setMedicalConditionsError] = useState<
        string[]
    >([]);
    const checkMedicalConditions = useCallback(
        (medicalConditions: string[]): string[] => {
            return medicalConditions.map((item) => {
                const trimmed = item.trim();
                if (trimmed.length === 0) {
                    return t(
                        'components.member_medical_settings_section.list_item_error_required'
                    );
                }
                if (trimmed.length > 100) {
                    return t(
                        'components.member_medical_settings_section.list_item_error_too_long'
                    );
                }
                return '';
            });
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setMedicalConditionsError(
                checkMedicalConditions(medicalConditions)
            );
        }
    }, [medicalConditions, showRealTimeErrors, checkMedicalConditions]);

    /**
     * Medications
     */
    const [medications, setMedications] = useState(initialMedications);
    const [medicationsError, setMedicationsError] = useState<string[]>([]);
    const checkMedications = useCallback(
        (medications: string[]): string[] => {
            return medications.map((item) => {
                const trimmed = item.trim();
                if (trimmed.length === 0) {
                    return t(
                        'components.member_medical_settings_section.list_item_error_required'
                    );
                }
                if (trimmed.length > 100) {
                    return t(
                        'components.member_medical_settings_section.list_item_error_too_long'
                    );
                }
                return '';
            });
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setMedicationsError(checkMedications(medications));
        }
    }, [medications, showRealTimeErrors, checkMedications]);

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (
            !checkAllergies(allergies).some((error) => error) &&
            !checkMedicalConditions(medicalConditions).some((error) => error) &&
            !checkMedications(medications).some((error) => error)
        ) {
            setShowRealTimeErrors(false);

            const bloodTypeValue = bloodType?.value ?? null;
            const allergiesTrimmed = allergies
                .map((f) => f.trim())
                .filter((f) => f.length > 0);
            const medicalConditionsTrimmed = medicalConditions
                .map((f) => f.trim())
                .filter((f) => f.length > 0);
            const medicationsTrimmed = medications
                .map((f) => f.trim())
                .filter((f) => f.length > 0);

            onSubmit?.({
                bloodType: bloodTypeValue,
                allergies: allergiesTrimmed,
                medicalConditions: medicalConditionsTrimmed,
                medications: medicationsTrimmed
            });
        }
    };

    function resetForm() {
        setBloodType(
            bloodTypeOptions.find((o) => o.value === initialBloodType)
        );
        setAllergies(initialAllergies);
        setAllergiesError(checkAllergies(initialAllergies));
        setMedicalConditions(initialMedicalConditions);
        setMedicalConditionsError(
            checkMedicalConditions(initialMedicalConditions)
        );
        setMedications(initialMedications);
        setMedicationsError(checkMedications(initialMedications));
        setShowRealTimeErrors(false);

        setIsEditing(false);
    }

    useImperativeHandle(ref, () => ({
        resetForm() {
            resetForm();
        }
    }));

    return (
        <UISection>
            <form onSubmit={handleSubmit} noValidate>
                <UISectionHeader
                    title={t(
                        'components.member_medical_settings_section.title'
                    )}
                    rightButtons={
                        isEditing
                            ? []
                            : [
                                  {
                                      text: t('shared.labels.edit'),
                                      variant: 'textDefault',
                                      type: 'button',
                                      disabled: !canEdit,
                                      disabledTooltip: editDisabledReason,
                                      onClick: () => {
                                          setIsEditing(true);
                                      }
                                  }
                              ]
                    }
                />
                <UISectionContent>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 24
                        }}
                    >
                        <Description />
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'flex-start',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.member_medical_settings_section.blood_type_label'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    {t(
                                        'components.member_medical_settings_section.blood_type_description'
                                    )}
                                </UIText>
                            </div>

                            <UISelectInput
                                required={false}
                                placeholder={t(
                                    'components.member_medical_settings_section.blood_type_placeholder'
                                )}
                                options={bloodTypeOptions}
                                value={bloodType}
                                onChange={setBloodType}
                                disabled={!isEditing}
                                style={{ display: 'flex', flex: 1 }}
                            />
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'flex-start',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.member_medical_settings_section.allergies_label'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    {t(
                                        'components.member_medical_settings_section.allergies_description'
                                    )}
                                </UIText>
                            </div>

                            <ListDataInput
                                name={t(
                                    'components.member_medical_settings_section.allergy_name'
                                )}
                                items={allergies}
                                setItems={setAllergies}
                                itemsError={allergiesError}
                                isEditing={isEditing}
                            />
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'flex-start',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.member_medical_settings_section.conditions_label'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    {t(
                                        'components.member_medical_settings_section.conditions_description'
                                    )}
                                </UIText>
                            </div>

                            <ListDataInput
                                name={t(
                                    'components.member_medical_settings_section.condition_name'
                                )}
                                items={medicalConditions}
                                setItems={setMedicalConditions}
                                itemsError={medicalConditionsError}
                                isEditing={isEditing}
                            />
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'flex-start',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.member_medical_settings_section.medications_label'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    {t(
                                        'components.member_medical_settings_section.medications_description'
                                    )}
                                </UIText>
                            </div>

                            <ListDataInput
                                name={t(
                                    'components.member_medical_settings_section.medication_name'
                                )}
                                items={medications}
                                setItems={setMedications}
                                itemsError={medicationsError}
                                isEditing={isEditing}
                            />
                        </div>
                    </div>
                </UISectionContent>
                {isEditing && (
                    <UISectionFooter
                        actions={[
                            {
                                variant: 'primary',
                                type: 'submit',
                                loading: loading,
                                disabled: loading,
                                text: loading
                                    ? t('shared.labels.saving')
                                    : t('shared.labels.save')
                            },
                            {
                                text: t('shared.labels.cancel'),
                                disabled: loading,
                                type: 'button',
                                onClick: resetForm
                            }
                        ]}
                    />
                )}
            </form>
        </UISection>
    );
});

type ListDataInputProps = {
    name: string;
    items: string[];
    setItems: Dispatch<SetStateAction<string[]>>;
    itemsError: string[];
    isEditing: boolean;
};
function ListDataInput(props: ListDataInputProps): ReactElement {
    const { name, items, setItems, itemsError, isEditing } = props;

    const { t } = useCompaniesTranslation();

    return (
        <div
            style={{
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                gap: 16
            }}
        >
            {items.map((item, index) => (
                <div
                    style={{
                        display: 'flex',
                        gap: 16,
                        alignItems: 'center'
                    }}
                >
                    <UIFormInput
                        name={`${name} ${index + 1}`}
                        value={`${isEditing ? '' : `${index + 1}. `}${item}`}
                        placeholder={`${name} ${index + 1}`}
                        type="text"
                        autoComplete="on"
                        errorText={itemsError[index]}
                        maxLength={100}
                        onChange={(value) => {
                            setItems((prev) => {
                                const newVals = [...prev];
                                newVals[index] = value;
                                return newVals;
                            });
                        }}
                        disabled={!isEditing}
                    />

                    {isEditing && (
                        <UIIconButton
                            icon="trash-2"
                            iconColor="white"
                            variant="danger"
                            type="button"
                            onClick={() => {
                                setItems((prev) =>
                                    prev.filter((_, i) => i !== index)
                                );
                            }}
                        />
                    )}
                </div>
            ))}
            {isEditing && items.length < 20 && (
                <div>
                    <UIButton
                        variant="textPrimary"
                        text={t(
                            'components.member_medical_settings_section.list_add_button',
                            {
                                name
                            }
                        )}
                        rightIcon="plus-circle"
                        type="button"
                        onClick={() => {
                            setItems((prev) => [...prev, '']);
                        }}
                    />
                </div>
            )}
            {!isEditing && items.length == 0 && (
                <UIFormInput value={t('shared.labels.none')} disabled={true} />
            )}
        </div>
    );
}

function Description(): ReactElement {
    const { t } = useCompaniesTranslation();

    return (
        <UIText type="default2">
            {t('components.member_medical_settings_section.intro')}
        </UIText>
    );
}
